import { Link } from "react-router-dom";

/**
 * Brand mark: rounded blue square with a paper-plane SVG,
 * next to the "getdeparto" wordmark.
 *
 * Props:
 *  - size: "sm" | "md" | "lg"   (default "md")
 *  - linked: boolean            (default true) — wraps in <Link to="/">
 *  - tagline: string?           (small secondary text right of wordmark, e.g. "/ Operations")
 */
export default function Logo({ size = "md", linked = true, tagline }) {
  const dims = {
    sm: { mark: 26, icon: 14, text: 18, radius: 7 },
    md: { mark: 36, icon: 18, text: 24, radius: 9 },
    lg: { mark: 44, icon: 22, text: 30, radius: 11 },
  }[size];

  const inner = (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 12,
        textDecoration: "none",
        color: "inherit",
      }}
      data-testid="brand-logo"
    >
      {/* Mark */}
      <span
        aria-hidden
        style={{
          width: dims.mark,
          height: dims.mark,
          borderRadius: dims.radius,
          background: "linear-gradient(135deg, #2563EB 0%, #1d4ed8 100%)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 6px 18px -6px rgba(37,99,235,0.45), inset 0 -1px 0 rgba(0,0,0,0.08)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Subtle trail line */}
        <span
          style={{
            position: "absolute",
            left: "12%",
            top: "60%",
            width: "40%",
            height: 1,
            background: "rgba(255,255,255,0.35)",
            borderRadius: 1,
            transform: "rotate(-18deg)",
          }}
        />
        {/* Paper plane */}
        <svg
          width={dims.icon}
          height={dims.icon}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ transform: "translateX(0.5px) translateY(-0.5px)" }}
        >
          <path
            d="M21.5 2.5L2.5 10.2c-.6.25-.55 1.12.08 1.3l6.6 1.9 1.9 6.6c.18.64 1.06.68 1.3.08L21.5 2.5z"
            fill="#ffffff"
          />
          <path
            d="M21.5 2.5L9.18 13.4"
            stroke="#2563EB"
            strokeWidth="1.2"
            strokeLinecap="round"
            opacity="0.5"
          />
        </svg>
      </span>

      {/* Wordmark */}
      <span
        style={{
          fontSize: dims.text,
          fontWeight: 700,
          letterSpacing: "-0.03em",
          lineHeight: 1,
          color: "#111827",
        }}
      >
        get<span style={{ color: "#2563EB" }}>departo</span>
      </span>

      {tagline && (
        <span
          style={{
            color: "#6B7280",
            fontWeight: 500,
            fontSize: Math.round(dims.text * 0.65),
            letterSpacing: "-0.01em",
            marginLeft: 4,
          }}
        >
          {tagline}
        </span>
      )}
    </span>
  );

  if (!linked) return inner;
  return (
    <Link to="/" style={{ textDecoration: "none", color: "inherit" }} aria-label="getdeparto home">
      {inner}
    </Link>
  );
}
